import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChatButtonProps {
    onClick: () => void;
    isOpen: boolean;
    hasUnread: boolean;
}

const SEEN_KEY = 'trbd_chat_seen';

const ChatButton = ({ onClick, isOpen, hasUnread }: ChatButtonProps) => {
    const [showNew, setShowNew] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const seen = window.localStorage.getItem(SEEN_KEY);
        if (!seen) setShowNew(true);
    }, []);

    useEffect(() => {
        if (isOpen && showNew && typeof window !== 'undefined') {
            window.localStorage.setItem(SEEN_KEY, '1');
            setShowNew(false);
        }
    }, [isOpen, showNew]);

    return (
        <div className="fixed z-50 bottom-[max(1.25rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] sm:bottom-6 sm:left-6">
            <div
                className="relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <motion.button
                    onClick={onClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-cursor="hover"
                    aria-label={isOpen ? 'إغلاق الدردشة' : 'فتح مساعد TRBD الذكي'}
                    className={cn(
                        "relative w-14 h-14 sm:w-16 sm:h-16 rounded-full",
                        "bg-gradient-to-br from-primary to-secondary",
                        "flex items-center justify-center",
                        "border border-white/20",
                        "transition-all duration-300",
                        !isOpen && "animate-fab-pulse-v2"
                    )}
                >
                    {/* Pulse Animation */}
                    {!isOpen && (
                        <>
                            <span className="absolute inset-0 rounded-full bg-primary/50 animate-ping opacity-30" />
                            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 animate-pulse" />
                        </>
                    )}

                    {/* Rotating AI ring when closed */}
                    {!isOpen && (
                        <motion.span
                            className="absolute -inset-1 rounded-full border border-white/20"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                            style={{
                                background: 'conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                                WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px))',
                                mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 1px))',
                            }}
                        />
                    )}

                    {/* Icon */}
                    <motion.div
                        animate={{ rotate: isOpen ? 90 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="relative"
                    >
                        {isOpen ? (
                            <X className="w-6 h-6 text-white" />
                        ) : (
                            <Sparkles className="w-6 h-6 text-white drop-shadow" />
                        )}
                    </motion.div>

                    {/* NEW badge */}
                    {showNew && !isOpen && (
                        <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-[9px] font-black text-background shadow-lg border border-white/30 tracking-wider"
                        >
                            NEW
                        </motion.span>
                    )}

                    {/* Unread Indicator */}
                    {hasUnread && !isOpen && !showNew && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full flex items-center justify-center">
                            <span className="w-2 h-2 bg-white rounded-full" />
                        </span>
                    )}
                </motion.button>

                {/* Tooltip */}
                <AnimatePresence>
                    {showTooltip && !isOpen && (
                        <motion.div
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -8 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-[64px] top-1/2 -translate-y-1/2 whitespace-nowrap"
                        >
                            <div className="px-3 py-2 rounded-xl bg-card/95 backdrop-blur-md border border-white/10 shadow-xl text-xs text-foreground">
                                اسألني أي شيء عن TRBD 🤖
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ChatButton;
